import { useMemo } from 'react'

import type { EChartsOption, LineSeriesOption } from 'echarts'

import { type PeriodWindow, buildMidnightMarkLineSeries, makeTimeAxis } from '@/components/charts/history/timeAxis'
import { ALERT_MARKER_TOP_PX, buildAlertMarkerSeries } from '@/insights/assets/historyGraph/alertMarkerSeries'
import { STATUS_AXIS_PX } from '@/insights/assets/historyGraph/constants'
import type { AlertMarker } from '@/insights/assets/historyGraph/types'
import { formatHistoryDateTime } from '@/utils/formatDate'

import {
  CANVAS_LEFT,
  CANVAS_RIGHT,
  CANVAS_TOP,
  FUEL_AREA_COLOR,
  FUEL_AXIS_MAX,
  FUEL_AXIS_MIN,
  FUEL_COLOR,
  // LOW_FUEL_COLOR,
  // LOW_FUEL_PERCENT,
} from './constants'
import type { ReeferFuelSample } from './types'

interface FuelPoint {
  value: [number, number]
  name: string
}

const toPoints = (samples: ReeferFuelSample[]): FuelPoint[] =>
  samples
    .filter((s) => s.fuelPercent != null)
    .map((s) => ({
      value: [new Date(s.sampleTime).getTime(), s.fuelPercent as number] as [number, number],
      name: s.location ?? '',
    }))
    .sort((a, b) => a.value[0] - b.value[0])

export function useFuelChartOptions(
  samples: ReeferFuelSample[] | undefined,
  periodWindow: PeriodWindow,
  zone: string,
  alertMarkers?: AlertMarker
): EChartsOption {
  return useMemo(() => {
    const points = toPoints(samples ?? [])

    const fuelSeries: LineSeriesOption = {
      type: 'line',
      name: 'Fuel',
      data: points,
      showSymbol: true,
      symbol: 'circle',
      symbolSize: 5,
      connectNulls: false,
      lineStyle: { width: 2, color: FUEL_COLOR },
      itemStyle: { color: FUEL_COLOR },
      areaStyle: { color: FUEL_AREA_COLOR },
      z: 3,
      // Low fuel line - Commenting out for now until further clarification (i anticipate it coming back once Kari determines where company.LowFuelAlertThreshold is being entered)
      // markLine: {
      //   silent: true,
      //   symbol: 'none',
      //   data: [
      //     {
      //       yAxis: LOW_FUEL_PERCENT,
      //       lineStyle: { color: LOW_FUEL_COLOR, type: 'dashed', width: 1 },
      //       label: { show: true, position: 'insideEndTop', formatter: `Low ${LOW_FUEL_PERCENT}%`, fontSize: 9 },
      //     },
      //   ],
      // },

      tooltip: {
        formatter: (params) => {
          const point = params as unknown as { value: [number, number]; name: string }
          const [time, percent] = point.value
          const lines = [`<b>Fuel</b>: ${percent.toFixed(1)}%`, formatHistoryDateTime(time, zone)]
          if (point.name) lines.push(point.name)
          return lines.join('<br/>')
        },
      },
    }

    const series: LineSeriesOption[] = [fuelSeries]
    const midnights = buildMidnightMarkLineSeries(periodWindow, zone, 0, 0)
    if (midnights) series.push(midnights)

    const alertMarkerSeries = buildAlertMarkerSeries(alertMarkers, periodWindow, zone)
    if (alertMarkerSeries) series.push(alertMarkerSeries)

    return {
      animation: false,
      grid: [
        {
          left: CANVAS_LEFT,
          right: CANVAS_RIGHT,
          top: alertMarkerSeries ? ALERT_MARKER_TOP_PX : CANVAS_TOP,
          bottom: STATUS_AXIS_PX,
        },
      ],
      xAxis: [makeTimeAxis({ gridIndex: 0, showLabel: true, periodWindow, zone })],
      yAxis: [
        {
          type: 'value',
          gridIndex: 0,
          min: FUEL_AXIS_MIN,
          max: FUEL_AXIS_MAX,
          interval: 25,
          axisLabel: { formatter: '{value}%' },
          name: 'Fuel %',
          nameLocation: 'middle',
          nameGap: 42,
          splitLine: { show: true, lineStyle: { color: '#d1d5db', opacity: 0.5 } },
        },
      ],
      tooltip: { trigger: 'item', confine: true },
      dataZoom: [{ type: 'inside', xAxisIndex: [0], filterMode: 'none', minSpan: 0, maxSpan: 100, throttle: 30 }],
      series,
    } as EChartsOption
  }, [samples, periodWindow, zone, alertMarkers])
}
